import React, {
  useRef,
  useCallback,
  useMemo,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  type CampaignDetailWidgetProps,
  type CampaignDetailThemingProps,
  CAMPAIGN_DETAIL_THEMING_PROP_TO_PARAM,
} from "./types";

/**
 * Ref handle exposed by LegionCampaignDetail for imperative control.
 */
export interface LegionCampaignDetailHandle {
  /** Reload the widget iframe */
  refresh: () => void;
}

/**
 * Build the iframe src URL from widget props.
 */
export function buildCampaignDetailWidgetUrl(
  props: CampaignDetailWidgetProps
): string {
  const { baseUrl, id, apiKey, theme = "light" } = props;

  const url = new URL(
    `/widget/campaign-detail/${encodeURIComponent(id)}`,
    baseUrl
  );
  url.searchParams.set("theme", theme);

  if (apiKey) {
    url.searchParams.set("apiKey", apiKey);
  }

  const themingKeys = Object.keys(
    CAMPAIGN_DETAIL_THEMING_PROP_TO_PARAM
  ) as Array<keyof CampaignDetailThemingProps>;
  for (const key of themingKeys) {
    const value = props[key];
    if (value !== undefined && value !== null && String(value).length > 0) {
      url.searchParams.set(
        CAMPAIGN_DETAIL_THEMING_PROP_TO_PARAM[key],
        String(value)
      );
    }
  }

  return url.toString();
}

/**
 * LegionCampaignDetail – a React component that embeds the Legion
 * Campaign Detail widget via an iframe. Designed to be opened inside
 * a host-controlled modal: pair with `onClose` for dismissal.
 *
 * @example
 * ```tsx
 * import { LegionCampaignDetail } from '@legionhandtech/lht-react-widgets';
 *
 * function App() {
 *   const [open, setOpen] = useState(false);
 *   const [campaignId, setCampaignId] = useState<string | null>(null);
 *
 *   return (
 *     <Modal opened={open} onClose={() => setOpen(false)}>
 *       {campaignId && (
 *         <LegionCampaignDetail
 *           id={campaignId}
 *           apiKey={token}
 *           baseUrl="https://your-legion-domain.com"
 *           onClose={() => setOpen(false)}
 *         />
 *       )}
 *     </Modal>
 *   );
 * }
 * ```
 */
export const LegionCampaignDetail = forwardRef<
  LegionCampaignDetailHandle,
  CampaignDetailWidgetProps
>(function LegionCampaignDetail(props, ref) {
  const {
    id,
    apiKey,
    theme = "light",
    height = "auto",
    width = "100%",
    className,
    style,
    onLoad,
    onError,
    onClose,
    onClaim,
    onRequestLogin,
    baseUrl,
    ...rest
  } = props;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        const iframe = iframeRef.current;
        if (iframe?.contentWindow) {
          iframe.contentWindow.location.reload();
        }
      },
    }),
    []
  );

  const src = useMemo(
    () => buildCampaignDetailWidgetUrl(props),
    [
      baseUrl,
      id,
      apiKey,
      theme,
      ...Object.keys(CAMPAIGN_DETAIL_THEMING_PROP_TO_PARAM).map(
        (k) => rest[k as keyof typeof rest]
      ),
    ]
  );

  // Listen for postMessage events from the iframe.
  useEffect(() => {
    if (typeof window === "undefined") return;

    let expectedOrigin: string | null = null;
    try {
      expectedOrigin = new URL(baseUrl).origin;
    } catch {
      expectedOrigin = null;
    }

    const handleMessage = (event: MessageEvent) => {
      // Reject all messages if expectedOrigin could not be derived from
      // baseUrl, otherwise a forged event from any page could trigger
      // onClose / onClaim / onRequestLogin in the host.
      if (!expectedOrigin || event.origin !== expectedOrigin) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "CAMPAIGN_DETAIL_CLOSE") {
        onClose?.();
      } else if (data.type === "CAMPAIGN_DETAIL_CLAIMED" && data.id) {
        onClaim?.({ id: String(data.id) });
      } else if (data.type === "CAMPAIGN_DETAIL_REQUEST_LOGIN") {
        onRequestLogin?.({
          id: data.id ? String(data.id) : null,
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [baseUrl, onClose, onClaim, onRequestLogin]);

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (iframe?.contentWindow && apiKey) {
      iframe.contentWindow.postMessage(
        { type: "CONFIG", apiKey, theme },
        baseUrl
      );
    }
    onLoad?.({ widget: "campaign-detail", id, theme });
  }, [apiKey, baseUrl, id, theme, onLoad]);

  const handleError = useCallback(() => {
    onError?.("Failed to load campaign detail widget");
  }, [onError]);

  const resolvedHeight =
    typeof height === "number" ? `${height}px` : height || "auto";
  const isFullHeight = resolvedHeight === "auto" || resolvedHeight === "100%";

  const containerStyle: React.CSSProperties = {
    display: "block",
    width,
    backgroundColor: "transparent",
    ...(isFullHeight ?
      { height: "100%", minHeight: "480px" }
    : { height: resolvedHeight }),
    ...style,
  };

  const iframeStyle: React.CSSProperties = {
    border: "none",
    borderRadius: "8px",
    overflow: "hidden",
    width: "100%",
    ...(isFullHeight ?
      { height: "100%", minHeight: "480px" }
    : { height: resolvedHeight }),
  };

  return (
    <div
      className={className}
      style={containerStyle}
      data-testid="legion-campaign-detail"
    >
      <iframe
        ref={iframeRef}
        src={src}
        title="Campaign Detail Widget"
        width="100%"
        loading="lazy"
        style={iframeStyle}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
});
